import React from 'react';
import { Bar, BarChart, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import styles from './BarChart.module.css';

const StackedBarChart = ({ graphData, chartHeight, sideTabVal }) => {

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '180px',
                    }}
                >
                    <p
                        style={{
                            margin: '0 0 8px 0',
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#333',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '6px',
                        }}
                    >
                        {label}
                    </p>
                    {payload.map((entry, index) => (
                        <div
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                margin: '6px 0',
                                fontSize: '11px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '2px',
                                        backgroundColor: entry.color,
                                        display: 'inline-block',
                                    }}
                                />
                                <span style={{ color: '#666' }}>{entry.name}:</span>
                            </div>
                            <span style={{ fontWeight: 600, color: '#333', marginLeft: '8px' }}>
                                {entry.value?.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2)}
                            </span>
                        </div>
                    ))}
                    {/* <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                            paddingTop: '6px',
                            borderTop: '1px solid #eee',
                            fontSize: '11px',
                        }}
                    >
                        <span style={{ color: '#333', fontWeight: 600 }}>Total:</span>
                        <span style={{ fontWeight: 700, color: '#000' }}>
                            {total.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2)}
                        </span>
                    </div> */}
                </div>
            );
        }
        return null;
    };

    const CustomLegend = (props) => {
        const { payload } = props;
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px'}}>
                {payload.map((entry, index) => {
                    const { value, color } = entry;
                    return (
                        <span
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '0 10px',
                            }}
                        >
                            <span
                                style={{
                                    backgroundColor: color,
                                    width: '6px',
                                    height: '12px',
                                    display: 'inline-block',
                                    borderRadius: '2px',
                                }}
                            />
                            <span style={{ color: '#4D4D4F' }}>{value}</span>
                        </span>
                    );
                })}
            </div>
        );
    };
    
 // Maximum height before scrolling kicks in
    const legendStyle = {
        fontSize: '10px',
        paddingTop: '5px',
        color: '#4D4D4F',
    };

    // Custom label to show total on top of bar
    const renderTotalLabel = (props) => {
        const { x, y, width, height, value, index } = props;
        const total = graphData[index]?.Total || 0;

        return (
            <text
                x={x + width + 5}
                y={y + height / 2}
                fill="#333"
                textAnchor="start"
                dominantBaseline="middle"
                fontSize="11px"
                fontWeight="600"
            >
                {total.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2)}
            </text>
        );
    };

    // Custom label for Min Dispatch Qty (dark blue bar)
    const renderMinLabel = (props) => {
        const { x, y, width, height, value, index } = props;
        
        if (!value || value === 0) return null;
        
        // For Min Dispatch Qty, value is correct as-is
        const displayValue = value;
        const formattedValue = displayValue.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2);
        
        // Estimate text width (approximate: 6-7 pixels per character for fontSize 10-11px)
        const estimatedTextWidth = formattedValue.length * 7;
        
        // Only render if the bar width is larger than the text width + padding
        if (width < estimatedTextWidth + 10) {
            return null;
        }
        
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#333"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10px"
                fontWeight="500"
            >
                {formattedValue}
            </text>
        );
    };

    // Custom label for Max Dispatch Qty (light blue bar)
    const renderMaxLabel = (props) => {
        const { x, y, width, height, index } = props;
        
        // Get the actual Max value directly from the data source
        const displayValue = graphData[index]?.["Max Dispatch Qty"] || 0;
        
        if (!displayValue || displayValue === 0) return null;
        
        const formattedValue = displayValue.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2);
        
        // Estimate text width (approximate: 6-7 pixels per character for fontSize 10-11px)
        const estimatedTextWidth = formattedValue.length * 7;
        
        // Only render if the bar width is larger than the text width + padding
        if (width < estimatedTextWidth + 10) {
            return null;
        }
        
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#333"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10px"
                fontWeight="500"
            >
                {formattedValue}
            </text>
        );
    };

    const renderNormLabel = (props) => {
        const { x, y, width, height, index } = props;
        
        // Get the actual Norm value directly from the data source
        const displayValue = graphData[index]?.["Norm Dispatch Qty"] || 0;
        
        if (!displayValue || displayValue === 0) return null;
        
        const formattedValue = displayValue.toFixed(sideTabVal !== 'Tonnes' ? 0 : 2);
        
        // Estimate text width (approximate: 6-7 pixels per character for fontSize 10-11px)
        const estimatedTextWidth = formattedValue.length * 7;
        
        // Only render if the bar width is larger than the text width + padding
        if (width < estimatedTextWidth + 10) {
            return null;
        }
        
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#333"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="10px"
                fontWeight="500"
            >
                {formattedValue}
            </text>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={chartHeight} style={{position:'relative'}}>
            <BarChart
                data={graphData}
                layout="vertical"
                barGap={5}
                barCategoryGap="30%"
                margin={{
                    top: 10,
                    right: 80,
                    left: 20,
                    bottom: 10,
                }}
            >
                <>
                    {/* <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} stroke="#CCC" horizontalPoints={10} /> */}
                    <XAxis type="number" domain={[0, 'auto']} tickLine={false} className={styles.BarLabelCBU} axisLine={{ strokeDasharray: "3 3", stroke: "#ccc" }}>
                        {/* <Label value="Quantity" position="insideBottom" offset={-5} className={styles.axisLabel} /> */}
                    </XAxis>
                    <YAxis dataKey="DC" type="category" interval={0} className={styles.BarLabelCBU} tickLine={false} axisLine={{ strokeDasharray: "3 3", stroke: "transparent" }}>
                        {/* <Label value="DC" angle={-90} position="insideLeft" className={styles.axisLabel} offset={-10} style={{ textAnchor: 'middle' }} /> */}
                    </YAxis>
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                    />
                    <Legend verticalAlign="bottom" align="left" wrapperStyle={legendStyle} content={<CustomLegend />} />
                    <Bar dataKey="Min Dispatch Qty" fill="#23E7FF" barSize={18} stackId="a" radius={[0, 0, 0, 0]} maxBarSize={18}>
                        <LabelList
                            content={renderMinLabel}
                        />
                    </Bar>
                    <Bar dataKey="Norm Dispatch Qty" name="Norm Dispatch Qty" fill="#e1f9fc" barSize={18} stackId="a" radius={[0, 4, 4, 0]} maxBarSize={18}>
                        <LabelList
                            content={renderNormLabel}
                        />
                    </Bar>
                    <Bar dataKey="Max Dispatch Qty" name="Max Dispatch Qty" fill="#B6F7FF" barSize={18} stackId="a" radius={[0, 0, 0, 0]} maxBarSize={18}>
                        <LabelList
                            content={renderMaxLabel}
                        />
                        {/* <LabelList
                            content={renderTotalLabel}
                        /> */}
                    </Bar>
                </>
            </BarChart>
        </ResponsiveContainer>
    );
}

export default StackedBarChart;